import { ActionContext } from 'vuex';
import { IClients } from '@llws/lift-entity-interfaces';
import { IApiDefaultMeta } from '@llws/platform-shared-types';
import { LongToken } from '@llws/platform-shared-types/types/auth';
import {
  INestedWhereCommand,
  IOrderCommand,
  IQueryCommandOptions,
  convertQueryCommandToQueryString,
  Equal,
} from '@llws/dynamic-router-utils';
import { IApiSingleResponse } from '@llws/platform-shared-types/types/response/IApiResponse';
import {
  CurrentUser,
  subsetObject,
  IApiResponse,
  IMetaResponse,
  ICurrentUserFormWithPasswords,
} from '@/shared';
import users from '@/shared/services/users';
import clients from '@/shared/services/clients';
import { adminRequest } from '@/shared/request-configs/admin-request';
import { storage } from '@/shared/helpers/storage';
import { IClientPremiumProductsResponse } from '@/shared/interfaces/premium-products';
import { oauthRequest } from '@/shared/request-configs/oauth-request';
import store from '..';

export type TUserClientMetadata = {
  infinityListings: number
  promotedListings: number
  marketInsightSubs: {
    cityId: number
    cityName: string
  }[]
};

export default {
  namespaced: true, // 'users/'

  state: {
    user: null,
    client: null as null | IApiResponse<IClients, IMetaResponse>,
    loading: false,
    userClientMetadata: null as null | TUserClientMetadata,
    accessibleClients: { data: [], meta: null },
    single: null,
    list: null,
    isAuthChecked: false,
  },

  getters: {
    current(state: any): CurrentUser | undefined {
      return state.user ? state.user : undefined;
    },

    currentClient(state: any): IClients | undefined {
      return state.user ? state.user?.client : undefined;
    },

    accessibleClients(state: any): IClients[] {
      return Array.isArray(state.accessibleClients?.data) ? [ ...state.accessibleClients.data ] : [];
    },

    client(state: any): IClients | null {
      return state.client?.data ?? null;
    },

    single(state: any) {
      return state.single?.data ? state.single.data : null;
    },

    list(state: any) {
      return state.list?.data ? state.list.data : [];
    },

    isAuthChecked(state: { isAuthChecked: boolean }) {
      return state.isAuthChecked;
    },

    account(state: any) {
      const { email, firstName, lastName } = state.user || {};
      const account = { email, firstName, lastName };
      return account;
    },

    userClientMetadata(state: { userClientMetadata: TUserClientMetadata }) {
      return state.userClientMetadata;
    },
  },
  mutations: {
    setCurrent(state: any, payload: any) {
      state.user = payload;
    },

    setAccessibleClients(state: any, payload: any) {
      state.accessibleClients = payload;
    },

    resetCurrent(state: any) {
      state.user = null;
    },

    setLoading(state: any, payload: boolean) {
      state.loading = payload;
    },

    setClient(state: any, payload: any) {
      if (payload.id) {
        state.client = { data: payload, meta: null };
      } else {
        state.client = payload;
      }
    },

    set(state: any, payload: IApiResponse<any, IMetaResponse>) {
      if (Array.isArray(payload.data || payload)) {
        state.list = payload.data ? payload : { data: payload, meta: null } as IApiResponse<any, null>;
      } else {
        state.single = payload.data ? payload : { data: payload, meta: null } as IApiResponse<any, null>;
      }
    },

    setIsAuthChecked(state: { isAuthChecked: boolean }, payload: boolean) {
      state.isAuthChecked = payload;
    },

    setUserClientMetadata(state: { userClientMetadata: TUserClientMetadata }, payload: IApiSingleResponse<TUserClientMetadata>) {
      if (payload.data) {
        state.userClientMetadata = payload.data;
      }
    },
  },
  actions: {
    async getCurrent(ctx: ActionContext<any, any>) {
      try {
        if (!ctx.getters.current && !ctx.getters.isAuthChecked) {
          const settings = storage.get('currentUserSettings');
          const isNewSession = storage.get('isNewSession');

          // Skip token refresh during impersonation and on initial login to avoid redundant refresh calls
          if (!settings?.impersonated && !isNewSession) {
            await users.token();
          }

          if (isNewSession) {
            storage.remove('isNewSession'); // Consume flag to resume normal refreshes
          }

          const result = await users.getCurrent();
          const permissionsResult = await adminRequest.get<LongToken['products']>('/v0/perms-api/user/products');
          const premiumProductsResult = await adminRequest.get<IClientPremiumProductsResponse>('/v0/perms-api/clients/premium-products');

          ctx.commit('setCurrent', new CurrentUser({
            ...result?.data,
            products: permissionsResult?.data,
            premiumProducts: premiumProductsResult?.data,
          }));
        }
      } catch (err) {
        console.error('User Auth Required');
      } finally {
        ctx.commit('setIsAuthChecked', true);
      }
    },

    async changeCurrentClient(ctx: ActionContext<any, any>, clientId: number): Promise<void> {
      return oauthRequest.post(`/auth/token/refresh`, { clientId });
    },

    async getUserClientMetadata(ctx: ActionContext<Record<string, unknown>, typeof store.state>) {
      if (ctx.getters.userClientMetadata) {
        return ctx.getters.userClientMetadata;
      }

      const result = await adminRequest.get<IApiSingleResponse<TUserClientMetadata>>(`/client-api/user-client-metadata`);
      ctx.commit('setUserClientMetadata', result);
      return result.data;
    },

    async getEditableClient(ctx: ActionContext<any, any>, clientId: Number): Promise<void> {
      const query: IQueryCommandOptions = {
        relations: [
          {
            path: 'city',
            alias: 'city',
          },
          {
            path: 'city.province',
            alias: 'province',
          },
        ],
      };
      const result = await adminRequest.get(`/client-api/clients/${clientId}${convertQueryCommandToQueryString(query)}`);
      ctx.commit('setClient', result);
    },

    async saveClient(ctx: ActionContext<any, any>, payload: IClients) {
      const subset = subsetObject(payload, ['city']);
      const result = await adminRequest.put(`/client-api/clients/${payload.id}`, subset);
      const current = ctx.getters.client;
      const currentUser = ctx.getters.current;
      currentUser.client = { ...currentUser.client, ...result.data, ...{ city: payload.city } };
      ctx.commit('setClient', { ...current, ...result.data, ...{ city: payload.city } });
      ctx.commit('setCurrent', currentUser);
    },

    async getAccessibleClients(ctx: ActionContext<any, any>, searchValue: string = '') {
      let result: IApiResponse<IClients[], Partial<IApiDefaultMeta>>;
      const valueSanitized = searchValue.replace(/&|\(|\)|\?/g, '');

      if (ctx.state.user?.isSuperUser) {
        // superUsers should have access to all clients

        let nestedWhere = valueSanitized ? {
          condition: 'and',
          whereCommands: [
            {
              condition: 'or',
              operator: 'like',
              field: 'c.name',
              value: valueSanitized,
            },
            {
              condition: 'or',
              operator: 'like',
              field: 'c.lwsCompanyLabel',
              value: valueSanitized,
            },
            {
              condition: 'or',
              operator: 'like',
              field: 'c.clientKey',
              value: valueSanitized,
            },
            {
              condition: 'or',
              field: 'c.id',
              value: valueSanitized,
            },
          ],
        } as INestedWhereCommand : null;

        let orderBy: IOrderCommand[] = [];

        if (window?.APP_CONFIG?.VUE_APP_ENV === 'demo') {
          // On the demo environment we only want to show select clients by default
          if (nestedWhere === null) {
            const demoIds = [ 8, 41, 69, 70, 67, 82, 92 ];
            nestedWhere = {
              condition: 'and',
              whereCommands: demoIds.map((id) => ({
                condition: 'or',
                field: 'id',
                value: `${id}`,
              })),
            };
          }

          orderBy = [
            { field: 'highPriority', direction: 'desc' },
            { field: 'id', direction: 'asc' },
          ];
        }

        result = await clients.get({
          orderBy,
          alias: 'c',
          limit: 20,
          select: [
            'c.id',
            'c.clientKey',
            'c.name',
            'c.lwsCompanyLabel',
            'c.serviceWebsite',
            'c.serviceRentsync',
            'c.highPriority',
            'cp.id',
            'p.id',
            'p.name',
            'p.partnerKey',
          ],
          relations: [
            { path: 'clientsPartners', alias: 'cp' },
            { path: 'cp.partner', alias: 'p' },
          ],
          ...nestedWhere && {
            where: [ Equal('c.status', 'active'), ...nestedWhere ? [nestedWhere] : [] ],
          },
        }) as unknown as IApiResponse<IClients[], IApiDefaultMeta>;

        result.data = (result.data ?? []).map((clientData: IClients) => ({
          ...clientData,
          isBundleClient: clientData.clientsPartners?.some((clientPartner) => clientPartner.partner?.partnerKey === 'rentals_infinity'),
        }) as IClients & { isBundleClient: boolean });
      } else {
        // users should only have access to clients they are authorized for
        const userClients: IClients[] = (await adminRequest.get('/v0/perms-api/user/clients-detail')).data;
        result = {
          data: (userClients ?? []).filter((client) => (
            Number(`${client.id}`) !== Number(ctx.state.user?.currentClientId)
          )),
          meta: {},
        };
      }

      ctx.commit('setAccessibleClients', result);
    },

    async getCurrentClient(_ctx: ActionContext<any, any>) {
      const result = await adminRequest.get(`/client-api/current-user/client-id`);
      return result;
    },

    async saveSelf(ctx: ActionContext<any, any>, payload: ICurrentUserFormWithPasswords) {
      const { current } = ctx.getters;

      await oauthRequest.post(`/auth/user`, payload);

      Object.assign(current, {
        firstName: payload.firstName,
        lastName: payload.lastName,
      });

      ctx.commit('setCurrent', current);
    },
  },
};
